import { useMutation, useQuery } from "@apollo/client";
import { useEffect, useMemo, useRef, useState } from "react";

import useLabel from "@apps/use-label/use-label";
import { errorNotificationVar, successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Input from "@components/input/input";

import {
  GetOrganizationIntegrationsQuery,
  GetOrganizationIntegrationsQueryVariables,
  IntegrationTypes,
} from "../../../../types/graphql-schema";
import getOrganizationIntegrations from "../../graphql/get-organization-integrations";
import removeGithubFromOrganizationMutation from "../../graphql/remove-github-from-organization-mutation";
import removeTeamsIntegrationMutation from "../../graphql/remove-teams-integration-mutation";
import removeYouTrackIntegrationMutation from "../../graphql/remove-youtrack-integration-mutation";
import saveFellowIntegrationMutation from "../../graphql/save-fellow-integration-mutation";
import unlinkOrganizationFromIntegrationMutation from "../../graphql/unlink-organization-from-integration-mutation";
import IntegrationDetailLayout from "./integration-detail-layout";
import { getNativeIntegrationsConfig } from "./integrations-config";

const buildConnectUrl = (slug: string, orgId: string) => {
  const listUrl = `/settings/organization/${orgId}/integrations?open=${slug}`;
  const next = encodeURIComponent(listUrl);
  switch (slug) {
    case "salesforce":
      return `/sso/salesforce?next=${next}&integration_link_to_org_id=${orgId}`;
    case "teams":
      return `/sso/microsoft?request_transcript_scopes=true&request_calendar_scope=true&request_bot_scope=true&next=/msteams/subscribe_to_transcripts?org_id=${orgId}&integration_link_to_org_id=${orgId}`;
    case "github":
      return import.meta.env.DEV
        ? `https://github.com/apps/topicflow-dev/installations/select_target`
        : `https://github.com/apps/topicflow/installations/select_target`;
    case "linear":
      return `/sso/linear?next=${next}&integration_link_to_org_id=${orgId}`;
    case "youtrack":
      return `/sso/youtrack?next=${next}&integration_link_to_org_id=${orgId}`;
    default:
      return "";
  }
};

const NativeIntegrationDetailPage = ({
  organization,
  slug,
  open,
  onClose,
}: {
  organization: any;
  slug: string;
  open: boolean;
  onClose: () => void;
}) => {
  const label = useLabel();
  const [fellowApiKey, setFellowApiKey] = useState("");
  const [fellowApiDomain, setFellowApiDomain] = useState(
    organization.fellowApiDomain || "",
  );
  const wasOpenRef = useRef(open);

  // Reset the Fellow credential fields each time the dialog is (re)opened so a
  // stale API key never lingers between sessions.
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setFellowApiKey("");
      setFellowApiDomain(organization.fellowApiDomain || "");
    }
    wasOpenRef.current = open;
  }, [open, organization.fellowApiDomain]);

  const { data: orgIntegrationsData, refetch } = useQuery<
    GetOrganizationIntegrationsQuery,
    GetOrganizationIntegrationsQueryVariables
  >(getOrganizationIntegrations, {
    variables: { organizationId: organization.id },
  });

  const [unlinkOrganizationFromIntegration] = useMutation(
    unlinkOrganizationFromIntegrationMutation,
    {
      onCompleted: () => {
        refetch();
        onClose();
      },
    },
  );
  const [removeGithub] = useMutation(removeGithubFromOrganizationMutation, {
    // hasGithubInstallation lives on the organization (getOrganizationSettings),
    // not the integrations list, so refetch that query to refresh the settings
    // modal. getLoggedInUser backs the homepage cards, so refetch it too.
    refetchQueries: ["getOrganizationSettings", "getLoggedInUser"],
    onCompleted: () => {
      successNotificationVar({ title: "GitHub integration removed" });
      onClose();
    },
  });
  const [removeTeams] = useMutation(removeTeamsIntegrationMutation, {
    refetchQueries: ["getOrganizationSettings", "getLoggedInUser"],
    onCompleted: () => {
      successNotificationVar({ title: "Teams integration removed" });
      onClose();
    },
  });
  const [removeYouTrack] = useMutation(removeYouTrackIntegrationMutation, {
    refetchQueries: [
      "getOrganizationSettings",
      "getLoggedInUser",
      "getOrganizationIntegrations",
    ],
    onCompleted: () => {
      successNotificationVar({ title: "YouTrack integration removed" });
      onClose();
    },
  });
  const [saveFellowIntegration, { loading: isSavingFellow }] = useMutation(
    saveFellowIntegrationMutation,
    { refetchQueries: ["getOrganizationSettings", "getLoggedInUser"] },
  );

  const config = useMemo(
    () => getNativeIntegrationsConfig(organization, label),
    [organization, label],
  );

  const integrationConfig = config[slug];

  const salesforceIntegration = useMemo(
    () =>
      orgIntegrationsData?.organization?.integrations.edges.find(
        (edge) => edge?.node?.integrationType === IntegrationTypes.Salesforce,
      )?.node,
    [orgIntegrationsData],
  );
  const linearIntegration = useMemo(
    () =>
      orgIntegrationsData?.organization?.integrations.edges.find(
        (edge) => edge?.node?.integrationType === IntegrationTypes.Linear,
      )?.node,
    [orgIntegrationsData],
  );

  const isConnected = (() => {
    switch (slug) {
      case "salesforce":
        return Boolean(salesforceIntegration);
      case "teams":
        return Boolean(
          organization.hasTeamsTranscriptConnection ||
            organization.hasTeamsInstallation,
        );
      case "github":
        return Boolean(organization.hasGithubInstallation);
      case "fellow":
        return Boolean(organization.hasFellowTranscriptSyncConnection);
      case "linear":
        return Boolean(linearIntegration);
      case "youtrack":
        return Boolean(organization.hasYoutrackIntegration);
      default:
        return false;
    }
  })();

  if (!integrationConfig) {
    return (
      <IntegrationDetailLayout
        open={open}
        onClose={onClose}
        name="Integration not found"
        logo=""
      >
        <p className="text-sm text-gray-500">
          We couldn’t find an integration with the slug “{slug}”.
        </p>
      </IntegrationDetailLayout>
    );
  }

  const handleConnect = () => {
    const url = buildConnectUrl(slug, organization.id);
    if (url) {
      window.location.href = url;
    }
  };

  const handleSaveFellow = () => {
    saveFellowIntegration({
      variables: {
        organizationId: organization.id,
        fellowApiKey: fellowApiKey.trim(),
        fellowApiDomain: fellowApiDomain.trim(),
        fellowTranscriptSyncEnabled: true,
      },
      onCompleted: () => {
        successNotificationVar({ title: "Fellow integration saved" });
        onClose();
      },
      onError: (error) => {
        errorNotificationVar({ title: error.message });
      },
    });
  };

  const handleDisconnect = () => {
    switch (slug) {
      case "salesforce":
        if (salesforceIntegration?.id) {
          unlinkOrganizationFromIntegration({
            variables: { organizationIntegrationId: salesforceIntegration.id },
          });
        }
        return;
      case "teams":
        removeTeams({ variables: { organizationId: organization.id } });
        return;
      case "github":
        removeGithub({ variables: { organizationId: organization.id } });
        return;
      case "fellow":
        saveFellowIntegration({
          variables: {
            organizationId: organization.id,
            fellowApiKey: "",
            fellowApiDomain: "",
            fellowTranscriptSyncEnabled: false,
          },
          onCompleted: () => {
            successNotificationVar({ title: "Fellow integration removed" });
            onClose();
          },
          onError: (error) => {
            errorNotificationVar({ title: error.message });
          },
        });
        return;
      case "linear":
        if (linearIntegration?.id) {
          unlinkOrganizationFromIntegration({
            variables: { organizationIntegrationId: linearIntegration.id },
          });
        }
        return;
      case "youtrack":
        removeYouTrack({ variables: { organizationId: organization.id } });
        return;
    }
  };

  const actions =
    slug === "fellow" ? (
      <>
        {isConnected && (
          <Button theme={buttonTheme.textRed} onClick={handleDisconnect}>
            Disconnect
          </Button>
        )}
        <Button
          theme={buttonTheme.primary}
          onClick={handleSaveFellow}
          disabled={
            isSavingFellow || !fellowApiKey.trim() || !fellowApiDomain.trim()
          }
        >
          {isConnected ? "Save" : "Connect"}
        </Button>
      </>
    ) : isConnected ? (
      <Button theme={buttonTheme.textRed} onClick={handleDisconnect}>
        Disconnect
      </Button>
    ) : (
      <Button theme={buttonTheme.primary} onClick={handleConnect}>
        Connect
      </Button>
    );

  return (
    <IntegrationDetailLayout
      open={open}
      onClose={onClose}
      name={integrationConfig.name}
      logo={integrationConfig.logo}
      description={integrationConfig.description}
      statusLabel={isConnected ? "Enabled" : "Not connected"}
      statusTone={isConnected ? "enabled" : "neutral"}
      actions={actions}
    >
      {slug === "fellow" && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Enter the Fellow API key and API domain for this organization.
            Topicflow will use them to import Fellow meeting transcripts.
          </p>
          <Input
            autoFocus
            label="API key"
            name="fellow-api-key"
            type="password"
            value={fellowApiKey}
            onChange={(event) => setFellowApiKey(event.target.value)}
            autoComplete="off"
          />
          <Input
            label="API domain"
            name="fellow-api-domain"
            type="url"
            value={fellowApiDomain}
            onChange={(event) => setFellowApiDomain(event.target.value)}
            placeholder="https://api.fellow.app"
            autoComplete="off"
          />
        </div>
      )}
    </IntegrationDetailLayout>
  );
};

export default NativeIntegrationDetailPage;
